import { ApiProvider } from '@/provider/ApiProvider';
import { setPreviewContextType, toContextType } from '@/auth/activeAssignment';
import { clearSession } from '@/auth/clearSession';
import {
  clearPreviewPages,
  hasPreviewPages,
  markLanding,
  notePreviewRunning,
  pageOf,
  pauseRolePages,
  readRolePage,
} from '@/auth/rolePages';
import { reloadToLanding } from '@/auth/roleSelection';
import {
  PREVIEW_ENDING_MESSAGE,
  PREVIEW_STARTING_MESSAGE,
  showRoleSwitchMessage,
} from '@/auth/roleSwitchProgress';
import { reasonMessage, toRefusal } from '@/pages/access-admin/refusals';

/**
 * Role preview (Authorization-Plan §13.7, owner design 2026-10-07): an
 * internal user with `role_preview` in an INTERNAL active role works in PNET
 * as a customer (or internal) role, for 30 minutes (extendable to at most
 * 2 hours from the start), without being assigned to the customer's
 * organisation. Full access (owner decision 2026-10-07): anything saved is
 * real, saved under the previewer's own user and recorded; a read-only
 * preview (`read_only`, identity PREVIEW_READ_ONLY) refuses every write.
 *
 *   GET    /identity/api/me/preview          {preview | null}
 *   POST   /identity/api/me/preview          {role_id, context_id?, reason?}
 *   POST   /identity/api/me/preview/extend   +30 minutes
 *   DELETE /identity/api/me/preview          end
 *
 * Starting, extending and ending reload the app's permissions and scope
 * (the effective access answers the previewed role while the preview runs).
 * While previewing, the legacy `userData.type` follows the previewed context
 * (activeAssignment.setPreviewContextType) and RolePreviewBanner shows
 * "Previewing <role> (<organisation>) — changes are saved for real and
 * recorded under your name — ends at HH:MM" (amber), or "— read only —"
 * (red) in a read-only preview.
 *
 * Pages (auth/rolePages.ts): a preview has its own page memory
 * (`pnet.rolepage.<user id>.preview`), so its navigation never overwrites the
 * user's own role's last page. Starting lands on the previewed role's home;
 * ending (the user, the time running out, or a role switch) forgets the
 * preview's pages and lands where the user was when the preview started:
 * the own role's last page, else its home.
 */

export const PERMISSION_ROLE_PREVIEW = 'role_preview';

export interface RolePreview {
  id: number;
  role_id: number;
  role_name: string;
  organisation: string | null;
  context_id: number;
  context_type: string;
  started_at: string | null;
  expires_at: string | null;
  /** seconds left when the identity service answered */
  expires_in: number;
  reason: string | null;
  read_only: boolean;
}

export type PreviewOutcome =
  | { ok: true; preview: RolePreview | null }
  | { ok: false; reason: string; message: string };

// ---- who may preview -------------------------------------------------------------------

/** An INTERNAL active role with role_preview (the server checks the same). */
export function canPreviewFrom(
  effective: { context?: { type?: unknown } | null; permissions?: unknown } | null | undefined,
): boolean {
  const permissions = Array.isArray(effective?.permissions) ? effective.permissions : [];
  return (
    String(effective?.context?.type ?? '') === 'INTERNAL' &&
    permissions.includes(PERMISSION_ROLE_PREVIEW)
  );
}

// ---- the current preview (shared by the banner and the pages) ---------------------------

let current: RolePreview | null = null;
let receivedAt = 0;
const listeners = new Set<(p: RolePreview | null) => void>();

export const getCurrentPreview = (): RolePreview | null => current;

/** ms timestamp at which the current preview ends (by the local clock). */
export const currentPreviewEndsAt = (): number | null =>
  current ? receivedAt + current.expires_in * 1000 : null;

export const subscribePreview = (listener: (p: RolePreview | null) => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export function setCurrentPreview(preview: RolePreview | null, now: number = Date.now()): void {
  current = preview;
  receivedAt = now;
  notePreviewRunning(preview !== null);
  setPreviewContextType(preview ? toContextType(preview.context_type) : null);
  listeners.forEach((l) => l(preview));
}

/** Test helper. */
export const resetPreviewForTests = (): void => {
  current = null;
  receivedAt = 0;
  listeners.clear();
};

// ---- server calls ------------------------------------------------------------------------

const toPreview = (v: unknown): RolePreview | null => {
  if (!v || typeof v !== 'object') return null;
  const p = v as Record<string, unknown>;
  const id = Number(p.id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    id,
    role_id: Number(p.role_id) || 0,
    role_name: String(p.role_name ?? ''),
    organisation:
      p.organisation === null || p.organisation === undefined ? null : String(p.organisation),
    context_id: Number(p.context_id) || 0,
    context_type: String(p.context_type ?? ''),
    started_at: (p.started_at as string | null) ?? null,
    expires_at: (p.expires_at as string | null) ?? null,
    expires_in: Math.max(0, Number(p.expires_in) || 0),
    reason: (p.reason as string | null) ?? null,
    read_only: p.read_only !== false,
  };
};

const outcome = (payload: unknown): PreviewOutcome => {
  if (payload && typeof payload === 'object' && 'preview' in (payload as object))
    return { ok: true, preview: toPreview((payload as { preview: unknown }).preview) };
  const refusal = toRefusal(payload ?? null);
  const reason = refusal?.reason || 'unavailable';
  return { ok: false, reason, message: refusal?.message || reasonMessage(reason) };
};

export async function fetchPreview(): Promise<PreviewOutcome> {
  return outcome(await ApiProvider.identityRequest('GET', 'me/preview'));
}

export async function startPreview(
  roleId: number,
  reason: string | null,
  contextId: number | null = null,
): Promise<PreviewOutcome> {
  const body: Record<string, unknown> = { role_id: roleId };
  if (contextId) body.context_id = contextId;
  if (reason && reason.trim() !== '') body.reason = reason.trim().slice(0, 255);
  return outcome(await ApiProvider.identityRequest('POST', 'me/preview', body));
}

export async function extendPreview(): Promise<PreviewOutcome> {
  return outcome(await ApiProvider.identityRequest('POST', 'me/preview/extend', {}));
}

export async function endPreview(): Promise<PreviewOutcome> {
  return outcome(await ApiProvider.identityRequest('DELETE', 'me/preview'));
}

// ---- flows -----------------------------------------------------------------------------------

/**
 * The preview's pages are still kept but the preview is over: it ran out, or
 * was ended, while no tab of it was open. The preview's pages go and the user
 * is back on the own role's last page (a refused page goes home, it is the
 * role's first landing).
 */
function returnFromEndedPreview(): void {
  if (!hasPreviewPages()) return;
  clearPreviewPages();
  const back = readRolePage();
  const here = pageOf(window.location.hash);
  if (back && back !== here) {
    markLanding(back);
    window.location.replace(`${window.location.pathname}${window.location.search}#${back}`);
  } else if (here) {
    markLanding(here);
  }
}

/** Page load (RoleGate, after the assignment is confirmed): the current preview, if any. */
export async function bootstrapPreview(): Promise<void> {
  try {
    const out = await fetchPreview();
    setCurrentPreview(out.ok ? out.preview : null);
    if (out.ok && out.preview === null) returnFromEndedPreview();
  } catch {
    setCurrentPreview(null);
  }
}

/**
 * After a start or an end: the role-dependent browser state is reset, the
 * permissions / brands of the (previewed or own) role are loaded again and
 * the app reloads on its landing page: at the start the previewed role's home
 * (the preview's page memory starts empty), at the end the page the preview
 * started from (the own role's last page, kept apart from the preview's), else
 * the own role's home.
 *
 * Before, the end landed on the home: the switch reset removed the one shared
 * `sessionurl`, and nothing else remembered the page the preview started from
 * (the preview's navigation had overwritten it anyway).
 */
export async function reloadForPreview(preview: RolePreview | null): Promise<void> {
  // the page on screen is neither role's from here until the reload: covered and unmounted
  showRoleSwitchMessage(preview ? PREVIEW_STARTING_MESSAGE : PREVIEW_ENDING_MESSAGE);
  pauseRolePages();
  clearSession('switch');
  clearPreviewPages();
  setCurrentPreview(preview);
  const user = ApiProvider.returnUserData();
  try {
    if (user?.id) {
      await ApiProvider.loadRoleData(
        Number(user.id),
        () => undefined,
        () => undefined,
      );
    }
  } catch {
    // the reload loads the role's data again at page load
  }
  reloadToLanding();
}

// ---- texts -----------------------------------------------------------------------------------

/** "14:35" (local time) of a ms timestamp. */
export const clockTime = (ms: number): string => {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** What a preview lets the previewer do, between the role and the end. */
export const PREVIEW_WRITABLE_TEXT = 'changes are saved for real and recorded under your name';
export const PREVIEW_READ_ONLY_TEXT = 'read only';

/**
 * "Previewing Adidas Marketing (Adidas) — changes are saved for real and
 * recorded under your name — ends at 14:35", or "— read only —" in a
 * read-only preview.
 */
export function previewBannerText(preview: RolePreview, endsAt: number): string {
  const org = preview.organisation ? ` (${preview.organisation})` : '';
  const mode = preview.read_only ? PREVIEW_READ_ONLY_TEXT : PREVIEW_WRITABLE_TEXT;
  return `Previewing ${preview.role_name}${org} — ${mode} — ends at ${clockTime(endsAt)}`;
}

/** Longest preview from its start (identity PREVIEW_HARD_CAP_MINUTES). */
export const PREVIEW_HARD_CAP_MINUTES = 120;

/** Whether "Extend 30 minutes" can still add time (local estimate; the server decides). */
export function canExtend(preview: RolePreview | null, endsAt: number | null): boolean {
  if (!preview || endsAt === null || !preview.started_at || !preview.expires_at)
    return Boolean(preview);
  const start = Date.parse(preview.started_at.replace(' ', 'T') + 'Z');
  const end = Date.parse(preview.expires_at.replace(' ', 'T') + 'Z');
  if (Number.isNaN(start) || Number.isNaN(end)) return true;
  return end - start < PREVIEW_HARD_CAP_MINUTES * 60_000;
}
